// ── Board DTOs ──────────────────────────────────────────────────────────────────
//
// API contracts — what clients send (BoardCreate/BoardUpdate) and receive (Board).
// No Mongoose, no DB details. Those live in board.model.ts.

import { z } from "zod";
import { Agent } from "./agent.type";
import { Profile } from "./profile.type";

// ── TagDefinition ──────────────────────────────────────────────────────────────

export const TagDefinitionColor = {
  GRAY: "gray",
  RED: "red",
  ORANGE: "orange",
  YELLOW: "yellow",
  GREEN: "green",
  BLUE: "blue",
  PURPLE: "purple",
  PINK: "pink",
  TEAL: "teal",
} as const;

export type TagDefinitionColor = (typeof TagDefinitionColor)[keyof typeof TagDefinitionColor];

export const TagDefinition = z.object({
  name: z.string().min(1).describe("Tag label"),
  color: z
    .enum(Object.values(TagDefinitionColor) as [string, ...string[]])
    .default("gray")
    .describe("Tag color — one of the predefined palette values"),
});
export type TagDefinition = z.infer<typeof TagDefinition>;

// ── Column ─────────────────────────────────────────────────────────────────────

export const Column = z.object({
  column_key: z.string().min(1).describe("Unique column identifier"),
  name: z.string().min(1).describe("Column display name"),
  process_stage: z.string().optional().describe("Associated process stage"),
  order: z.number().int().describe("Column display order"),
});
export type Column = z.infer<typeof Column>;

// ── Board ──────────────────────────────────────────────────────────────────────

export const Board = z.object({
  id: z.string().describe("Unique resource identifier"),
  team_id: z.string().min(1).describe("Owning team ID"),
  owner_id: z.string().min(1).describe("Owner user ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  name: z.string().min(1).max(100).describe("Board display name"),
  description: z.string().optional().describe("Board description"),
  alias: z.string().optional().describe("URL-safe board slug"),
  columns: z.array(Column).optional().describe("Board columns definition"),
  agent_ids: z.array(z.string()).optional().describe("Assigned agent IDs"),
  member_ids: z.array(z.string()).optional().describe("Board member user IDs"),
  allowed_roles: z.array(z.string()).optional().describe("Roles allowed to access this board"),
  tags: z.array(z.string()).optional().describe("Board tags"),
  tag_definitions: z
    .array(TagDefinition)
    .optional()
    .describe("Board tag definitions with name and color"),
  dashboard_id: z.string().nullable().optional().describe("Linked dashboard ID"),
  is_favorite: z.boolean().optional().describe("Whether board is marked as favorite"),
  active_cards: z.number().int().optional().describe("Count of non-deleted cards on this board"),
  deleted_cards: z
    .number()
    .int()
    .optional()
    .describe("Count of soft-deleted cards in the recycle bin"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=owner. */
  owner: Profile.partial()
    .optional()
    .describe("Embedded owner profile — present only when ?embed=owner"),
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=agents. */
  agents: z
    .array(Agent.partial())
    .optional()
    .describe("Embedded agents array — present only when ?embed=agents"),
});
export type Board = z.infer<typeof Board>;

// BoardCreate — all writable fields (server sets id, created_at, updated_at)
export const BoardCreate = Board.omit({
  id: true,
  created_at: true,
  updated_at: true,
  active_cards: true,
  deleted_cards: true,
  // embed fields — computed by the repo, never sent by the client
  owner: true,
  agents: true,
});
export type BoardCreate = z.infer<typeof BoardCreate>;

// BoardUpdate — same as BoardCreate minus immutable fields, all optional.
export const BoardUpdate = BoardCreate.omit({
  team_id: true,
  owner_id: true,
  created_by: true,
  alias: true,
}).partial();
export type BoardUpdate = z.infer<typeof BoardUpdate>;
